/**
 * global.js — Theme utility functions & custom elements
 * Required by facets.js for collection page filter functionality.
 * This file was accidentally overwritten and has been restored.
 */

/**
 * debounce
 * Returns a function that delays invoking `fn` until `wait` ms after the
 * last time it was called. Used by FacetFiltersForm to batch filter changes.
 */
function debounce(fn, wait) {
  var timeout;
  return function() {
    var context = this;
    var args = arguments;
    clearTimeout(timeout);
    timeout = setTimeout(function() {
      fn.apply(context, args);
    }, wait);
  };
}

/**
 * onKeyUpEscape
 * Closes the nearest details element when Escape is pressed.
 * Used in facets filter dropdowns.
 */
function onKeyUpEscape(event) {
  if (event.code && event.code.toUpperCase() !== 'ESCAPE') return;
  var openDetails = event.target.closest('details[open]');
  if (!openDetails) return;
  var summaryElement = openDetails.querySelector('summary');
  openDetails.removeAttribute('open');
  if (summaryElement) summaryElement.setAttribute('aria-expanded', 'false');
  summaryElement && summaryElement.focus();
}

/**
 * FocusHelper
 * Traps / restores focus for drawer-style elements.
 */
var trapFocusHandlers = {};

function trapFocus(container, elementToFocus) {
  elementToFocus = elementToFocus || container;
  var elements = Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
    )
  );
  var first = elements[0];
  var last  = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = function(event) {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code && event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout',  trapFocusHandlers.focusout);
  document.addEventListener('focusin',   trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus) {
  document.removeEventListener('focusin',  trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown',  trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

/**
 * pauseAllMedia
 * Pauses all videos and deferred media on the page.
 * Called when modals / drawers open.
 */
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach(function(video) {
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach(function(video) {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach(function(video) {
    video.pause();
  });
  document.querySelectorAll('product-model').forEach(function(media) {
    if (media.mediaObserver) media.mediaObserver.pause();
  });
}

/**
 * HTMLUpdateUtility
 * Helpers to swap DOM content returned from AJAX fetch responses.
 */
function fetchConfig(type) {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/' + (type || 'json') }
  };
}

/**
 * DetailsDisclosure — <details-disclosure> custom element.
 * Handles keyboard navigation for disclosure widgets.
 */
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.querySelector('details > *:last-child');
    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(function() {
      if (!this.contains(document.activeElement)) this.close();
    }.bind(this));
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (this.mainDetailsToggle.open) {
      this.animations.forEach(function(a) { a.play(); });
    } else {
      this.animations.forEach(function(a) { a.cancel(); });
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'false');
  }
}
customElements.define('details-disclosure', DetailsDisclosure);

/**
 * HeaderMenu — <header-menu> custom element.
 */
class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      Math.floor(this.header.getBoundingClientRect().bottom) + 'px'
    );
  }
}
customElements.define('header-menu', HeaderMenu);

/**
 * MenuDrawer — <menu-drawer> custom element.
 * Used by the mobile filter facet form. bindEvents() is called by
 * FacetFiltersForm.renderAdditionalElements() after each filter update.
 */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.bindEvents();
  }

  bindEvents() {
    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.addEventListener('keyup', onKeyUpEscape);
    }
    var summaries = this.querySelectorAll('summary');
    summaries.forEach(function(summary) {
      summary.addEventListener('click', this.onSummaryClick.bind(this));
      summary.addEventListener('keyup', onKeyUpEscape);
    }.bind(this));

    var buttons = this.querySelectorAll('button:not(.localization-selector)');
    buttons.forEach(function(button) {
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    }.bind(this));
  }

  onKeyUp(event) {
    if (event.code && event.code.toUpperCase() !== 'ESCAPE') return;
    var openDetailsElements = this.querySelectorAll('details[open]');
    var summaryElements = this.querySelectorAll('details[open] summary');
    openDetailsElements.forEach(function(el) { el.removeAttribute('open'); });
    summaryElements.forEach(function(el) { el.setAttribute('aria-expanded', 'false'); });
  }

  onSummaryClick(event) {
    var summary = event.currentTarget;
    var details = summary.parentNode;
    var parentMenuDrawer = details.closest('.menu-drawer');
    var isOpen = details.open;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(parentMenuDrawer || details, summary);
      details.removeEventListener('transitionend', addTrapFocus);
    }

    if (details.open) {
      removeTrapFocus();
      this.closeAnimation(details);
    } else {
      details.setAttribute('open', true);
      parentMenuDrawer
        ? details.addEventListener('transitionend', addTrapFocus)
        : trapFocus(details, summary);
      this.openAnimation(details);
    }
  }

  onCloseButtonClick(event) {
    var details = event.currentTarget.closest('details');
    this.closeSubmenu(details);
  }

  closeSubmenu(details) {
    var parentMenuDrawer = details.closest('.menu-drawer');
    removeTrapFocus(details.querySelector('summary'));
    this.closeAnimation(details);
    if (parentMenuDrawer) trapFocus(parentMenuDrawer, parentMenuDrawer.querySelector('summary'));
  }

  openAnimation(details) {
    var navWrapper = details.querySelector('.menu-drawer__submenu--last');
    var summaryElement = details.querySelector('summary');
    details.style.clipPath = 'inset(0 0 100% 0)';

    var animation = details.animate(
      [{ clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0)' }],
      { duration: 300, easing: 'ease' }
    );
    animation.onfinish = function() {
      details.style.clipPath = '';
      if (navWrapper) trapFocus(navWrapper, navWrapper.querySelector('[role="menuitem"]'));
    };
    animation.oncancel = function() {
      details.style.clipPath = '';
    };
  }

  closeAnimation(details) {
    var animation = details.animate(
      [{ clipPath: 'inset(0)' }, { clipPath: 'inset(0 0 100% 0)' }],
      { duration: 300, easing: 'ease' }
    );
    animation.onfinish = function() {
      details.removeAttribute('open');
    };
  }
}
customElements.define('menu-drawer', MenuDrawer);

/**
 * HeaderDrawer — <header-drawer> custom element.
 */
class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.header = document.querySelector('#shopify-section-header');
    this.borderOffset = this.closest('[data-header-offset]');
    this.addEventListener('keyup', function(evt) { if (evt.code === 'Escape') this.closeMenuDrawer(evt); }.bind(this));
    this.querySelector('#DetailsMobile').addEventListener('keyup', function(evt) {
      if (evt.code === 'Escape') evt.target.closest('details').open = false;
    });
  }

  openMenuDrawer(summaryElement) {
    this.openAnimation(this.mainDetailsToggle);
    this.mainDetailsToggle.setAttribute('open', '');
    if (this.header) this.header.classList.add('menu-open');
    summaryElement.setAttribute('aria-expanded', 'true');
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('overflow-hidden');
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    this.closeAnimation(this.mainDetailsToggle);
    if (this.header) this.header.classList.remove('menu-open');
    if (event) event.target.closest('details').open = false;
    if (elementToFocus) removeTrapFocus(elementToFocus);
    document.body.classList.remove('overflow-hidden');
  }

  onSummaryClick(event) {
    event.preventDefault();
    var summaryElement = event.currentTarget;
    this.mainDetailsToggle.hasAttribute('open')
      ? this.closeMenuDrawer(event, summaryElement)
      : this.openMenuDrawer(summaryElement);
  }

  onKeyUp(event) {
    if (event.code && event.code.toUpperCase() !== 'ESCAPE') return;
    this.closeMenuDrawer();
    this.mainDetailsToggle.querySelector('summary').focus();
  }
}
customElements.define('header-drawer', HeaderDrawer);

/**
 * SliderComponent — <slider-component> custom element.
 * Handles swipeable carousels (not related to facets but part of global.js).
 */
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    if (!this.slider || !this.sliderItems) return;

    this.initPages();
    var resizeObserver = new ResizeObserver(function() { this.initPages(); }.bind(this));
    resizeObserver.observe(this.slider);
    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    if (!this.prevButton || !this.nextButton) return;
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(function(el) {
      return el.clientWidth > 0;
    });
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;
    var currentPage = Math.ceil(this.slider.scrollLeft / this.sliderItemOffset);
    this.currentPage = currentPage + 1;
    if (this.currentPage === 1) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }
    if (this.currentPage === this.totalPages) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    var step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slider.scrollTo({ left: this.slideScrollPosition });
  }
}
customElements.define('slider-component', SliderComponent);

/**
 * SlideshowComponent — <slideshow-component> custom element.
 */
class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = false;
    if (!this.slider || !this.nextButton) return;
    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length === 1) return;
    this.changeEvents();
    if (this.sliderControlWrapper) {
      this.sliderControlWrapper.removeAttribute('hidden');
      this.initTimer();
    }
  }

  changeEvents() {
    this.slider.addEventListener('slide:resume', this.initTimer.bind(this));
    this.slider.addEventListener('slide:pause', this.pauseTimer.bind(this));
  }

  initTimer() {
    var autoplay = this.slider.dataset.autoplay;
    if (!autoplay || autoplay === 'false') return;
    var delay = this.slider.dataset.speed;
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.timer = setInterval(function() {
      this.nextButton.dispatchEvent(new Event('click'));
    }.bind(this), delay);
  }

  pauseTimer() {
    if (this.timer) clearInterval(this.timer);
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.pauseTimer();
    event.currentTarget.addEventListener('transitionend', this.initTimer.bind(this), { once: true });
  }
}
customElements.define('slideshow-component', SlideshowComponent);

/**
 * VariantSelects — <variant-selects> custom element.
 */
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), function(select) {
      return select.value;
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find(function(variant) {
      return !variant.options.some(function(option, index) {
        return this.options[index] !== option;
      }.bind(this));
    }.bind(this));
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    var mediaGallery = document.getElementById('MediaGallery-' + this.dataset.section);
    if (mediaGallery) mediaGallery.setActiveMedia('{{ section.id }}-' + this.currentVariant.featured_media.id, true);
    var modalContent = document.querySelector('#ProductModal-' + this.dataset.section + ' .product-media-modal__content');
    if (!modalContent) return;
    var newMediaModal = modalContent.querySelector('[data-media-id="' + this.currentVariant.featured_media.id + '"]');
    if (newMediaModal) modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', this.dataset.url + '?variant=' + this.currentVariant.id);
  }

  updateVariantInput() {
    var productForms = document.querySelectorAll('#product-form-' + this.dataset.section + ', #product-form-installment-' + this.dataset.section);
    productForms.forEach(function(productForm) {
      var input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }.bind(this));
  }

  updatePickupAvailability() {
    var pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;
    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    var section = this.closest('section');
    if (!section) return;
    var productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(this.dataset.url + '?variant=' + this.currentVariant.id + '&section_id=' + this.dataset.originalSection)
      .then(function(response) { return response.text(); })
      .then(function(responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        var priceDestination = document.getElementById('price-' + this.dataset.section);
        var priceSource      = html.getElementById('price-' + this.dataset.originalSection);
        if (priceSource && priceDestination) priceDestination.innerHTML = priceSource.innerHTML;

        var inventoryDestination = document.getElementById('Inventory-' + this.dataset.section);
        var inventorySource      = html.getElementById('Inventory-' + this.dataset.originalSection);
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;

        var skuDestination = document.getElementById('Sku-' + this.dataset.section);
        var skuSource      = html.getElementById('Sku-' + this.dataset.originalSection);
        if (skuSource && skuDestination) skuDestination.innerHTML = skuSource.innerHTML;

        this.toggleAddButton(!this.currentVariant.available, window.variantStrings && window.variantStrings.soldOut);
      }.bind(this));
  }

  toggleAddButton(disable, text, modifyClass) {
    var productForm = document.getElementById('product-form-' + this.dataset.section);
    if (!productForm) return;
    var addButton     = productForm.querySelector('[name="add"]');
    var addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;
    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings && window.variantStrings.addToCart;
    }
    if (modifyClass === false) return;
  }

  setUnavailable() {
    var button   = document.getElementById('product-form-' + this.dataset.section);
    var addButton     = button && button.querySelector('[name="add"]');
    var addButtonText = button && button.querySelector('[name="add"] > span');
    var price = document.getElementById('price-' + this.dataset.section);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings && window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}
customElements.define('variant-selects', VariantSelects);

/**
 * VariantRadios — <variant-radios> custom element.
 * Same as VariantSelects but reads radio inputs.
 */
class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    var fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map(function(fieldset) {
      return Array.from(fieldset.querySelectorAll('input')).find(function(radio) {
        return radio.checked;
      }).value;
    });
  }
}
customElements.define('variant-radios', VariantRadios);
